import { useState, useEffect } from 'react';
import { Pagination } from '@/components/ui/pagination';
import { useConversationHistory } from '@/hooks/useConversationHistory';
import { Button } from '@/components/ui/button';
import { X, Eye, MessagesSquare, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

interface ConversationThread {
  id: string;
  user: string;
  preview: string;
  messages: number;
  tokens: number;
  duration: number;
  status: 'SUCCESS' | 'ERROR' | 'TIMEOUT';
  started: string;
}

const transformConversationData = (apiData: any[]): ConversationThread[] => {
  return apiData.map((conversation) => {
    const firstMessage = conversation.messages?.[0];
    const content = firstMessage?.content || '';
    const preview =
      content.length > 0
        ? content.substring(0, 50) + (content.length > 50 ? '...' : '')
        : 'No preview available';

    const duration = conversation.usage?.duration || 0;
    const hasErrors = conversation.traces?.some((trace: any) => {
      if (typeof trace === 'string') {
        return trace.toLowerCase().includes('error') || trace.toLowerCase().includes('exception');
      }
      if (typeof trace === 'object' && trace !== null) {
        const traceString = JSON.stringify(trace).toLowerCase();
        return traceString.includes('error') || traceString.includes('exception');
      }
      return false;
    });
    const status: ConversationThread['status'] = hasErrors ? 'ERROR' : 'SUCCESS';

    return {
      id:
        conversation.threadId?.substring(0, 12) +
        '...' +
        conversation.threadId?.substring(conversation.threadId.length - 4) || 'unknown',
      user: conversation.user || 'unknown@example.com',
      preview,
      messages: conversation.messages?.length || 0,
      tokens: conversation.usage?.totalTokens || 0,
      duration: Math.round(duration * 100) / 100,
      status,
      started: new Date(conversation.start)
        .toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        })
        .replace(',', ''),
    };
  });
};

const StatusBadge = ({ status }: { status: ConversationThread['status'] }) => {
  const getStatusStyles = () => {
    switch (status) {
      case 'SUCCESS':
        return 'bg-secondary/10 text-secondary font-bold';
      case 'ERROR':
        return 'bg-destructive/10 text-destructive font-bold';
      case 'TIMEOUT':
        return 'bg-yellow-100 text-yellow-800 font-bold';
      default:
        return 'bg-muted text-muted-foreground font-bold';
    }
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusStyles()}`}>
      {status}
    </span>
  );
};

const formatDuration = (seconds: number) => {
  if (seconds <= 0 || isNaN(seconds)) return '—';
  seconds = Number(seconds.toFixed(2));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h > 0
    ? `${h}h ${m}m ${s}s`
    : m > 0
      ? `${m}m ${s}s`
      : `${s}s`;
}

const MessageBubble = ({ message, index }: { message: any; index: number }) => {
  const isUser = message.type === 'user';
  const isAssistant = message.type === 'assistant_message';

  return (
    <motion.div
      className={`mb-4 ${isUser ? 'text-right' : 'text-left'}`}
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.3,
        delay: index * 0.1,
        ease: 'easeOut',
      }}
    >
      <motion.div
        className={`inline-block max-w-[80%] p-3 rounded-lg ${isUser
          ? 'bg-primary text-primary-foreground'
          : isAssistant
            ? 'bg-muted text-foreground'
            : 'bg-muted/50 text-muted-foreground'
          }`}
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.2 }}
      >
        <div className="mb-1 text-sm font-medium">
          {isUser ? 'User' : isAssistant ? 'Assistant' : message.type}
        </div>
        <div className="text-sm whitespace-pre-wrap">{message.content}</div>
        <div className={`text-xs mt-2 opacity-70 ${isUser ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
          {new Date(message.start).toLocaleString()}
        </div>
      </motion.div>
    </motion.div>
  );
};

const ConversationPanel = ({
  conversation,
  isOpen,
  onClose,
}: {
  conversation: any;
  isOpen: boolean;
  onClose: () => void;
}) => {
  return (
    <AnimatePresence>
      {isOpen && conversation && (
        <motion.div
          className="overflow-hidden fixed inset-0 z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />

          {/* Panel */}
          <motion.div
            className="absolute top-0 right-0 w-1/2 h-full bg-background shadow-xl border-l border-border"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{
              type: 'spring',
              damping: 25,
              stiffness: 200,
              duration: 0.3,
            }}
          >
            <div className="flex flex-col h-full">

              {/* Header */}
              <motion.div
                className="flex justify-between items-center p-4 border-b border-border"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.3 }}
              >
                <div>
                  <h2 className="text-lg font-semibold text-foreground">{conversation.user}</h2>
                  <p className="text-sm text-muted-foreground">Thread: {conversation.threadId} • Started at {new Date(conversation.start).toLocaleString()}</p>
                </div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button variant="ghost" size="sm" onClick={onClose} className="p-2">
                    <X className="w-5 h-5" />
                  </Button>
                </motion.div>
              </motion.div>

              <div className="flex gap-6 p-4">
                {/* Messages Pod */}
                <div className="flex-1 bg-muted/50 rounded-lg px-6 py-4 shadow-sm">
                  <div className="text-xs font-medium text-muted-foreground mb-2">Messages</div>
                  <div className="text-2xl font-bold text-foreground">
                    {conversation.messages?.length ?? 0}
                  </div>
                </div>
                {/* Tokens Pod */}
                <div className="flex-1 bg-muted/50 rounded-lg px-6 py-4 shadow-sm">
                  <div className="text-xs font-medium text-muted-foreground mb-2">Total Tokens</div>
                  <div className="text-2xl font-bold text-foreground">
                    {conversation.usage?.totalTokens ?? 0}
                  </div>
                </div>
                {/* Duration Pod */}
                <div className="flex-1 bg-muted/50 rounded-lg px-6 py-4 shadow-sm">
                  <div className="text-xs font-medium text-muted-foreground mb-2">LLM Duration</div>
                  <div className="text-2xl font-bold text-foreground">
                    {(() => {
                      return formatDuration(conversation.usage?.duration || 0);
                    })()}
                  </div>
                </div>
              </div>

              {/* Content */}
              <motion.div
                className="overflow-y-auto flex-1 p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.3 }}
              >
                {/* Conversation Info */}
                <motion.div
                  className="p-4 mb-6 bg-muted/30 rounded-lg border border-border"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.3 }}
                >
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-muted-foreground uppercase text-[10px] tracking-wider">Model</span>
                      <p className="text-foreground font-medium">{conversation.usage?.model}</p>
                    </div>
                    <div>
                      <span className="font-medium text-muted-foreground uppercase text-[10px] tracking-wider">Last Activity</span>
                      <p className="text-foreground font-medium">
                        {new Date(conversation.messages[conversation.messages.length - 1].start).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-muted-foreground uppercase text-[10px] tracking-wider">Messages</span>
                      <p className="text-foreground font-medium">{conversation.messages?.length || 0}</p>
                    </div>
                    <div>
                      <span className="font-medium text-muted-foreground uppercase text-[10px] tracking-wider">Total Tokens</span>
                      <p className="text-foreground font-medium">
                        {conversation.usage?.totalTokens?.toLocaleString() || 0}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-muted-foreground uppercase text-[10px] tracking-wider">TTFT</span>
                      <p className="text-foreground font-medium">{(() => {
                        if (!conversation.usage?.timeToFirstToken) return '—';
                        const llmStart = conversation.traces[0].find((trace: any) => trace.name === 'LLM Execution')?.starts_at * 1000;
                        const seconds = (llmStart + conversation.usage.timeToFirstToken) / 1000;
                        return `${formatDuration(seconds)}`;
                      })()}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-muted-foreground uppercase text-[10px] tracking-wider">Throughput</span>
                      <p className="text-foreground font-medium">
                        {conversation.usage?.tokenThroughput?.toLocaleString() || 0} tps
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Messages */}
                <motion.div
                  className="space-y-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.3 }}
                >
                  <h3 className="font-bold text-foreground text-md uppercase tracking-tight">Messages</h3>
                  {conversation.messages?.map((message: any, index: number) => (
                    <MessageBubble key={index} message={message} index={index} />
                  ))}
                </motion.div>

                <motion.div
                  className="mt-8"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45, duration: 0.3 }}
                >
                  <h3 className="mb-4 font-bold text-foreground text-md uppercase tracking-tight">Performance Traces</h3>
                  {conversation.traces && conversation.traces.length > 0 ? (
                    conversation.traces.map((traceSequence: any[], traceIdx: number) => (
                      <div key={traceIdx} className="mb-3">
                        {conversation.traces.length > 1 && (
                          <div className="mb-1 text-xs text-gray-500 font-mono">
                            Trace Group {traceIdx + 1}
                          </div>
                        )}
                        <div className="w-full overflow-x-auto">
                          <div className="flex flex-col gap-2">
                            {traceSequence.map((trace, i) => (
                              <div
                                key={i}
                                className="flex items-center gap-4 px-4 py-2 rounded bg-muted/50 border border-border"
                                style={{ fontFamily: "Menlo, monospace" }}
                              >
                                {/* Trace name - static on left */}
                                <div className="flex-none w-48 text-sm font-semibold text-foreground truncate">
                                  {trace.name}
                                </div>
                                {/* Timeline visual */}
                                <div className="flex-1 min-w-0">
                                  <div className="relative h-4 w-full bg-muted rounded overflow-hidden">
                                    <div
                                      className="absolute left-0 top-0 h-4 rounded bg-secondary"
                                      style={{
                                        left: `${trace.starts_percentage}%`,
                                        width: `${Math.max(1, trace.duration_percentage)}%`,
                                      }}
                                    ></div>
                                  </div>
                                </div>
                                {/* Timing info */}
                                <div className="flex-none w-32 text-xs text-right text-muted-foreground">
                                  <div className="text-foreground">{typeof trace.starts_at === "number" ? trace.starts_at.toFixed(3) : "0.000"}s</div>
                                  <div>
                                    {typeof trace.duration === "number" ? trace.duration.toFixed(3) : "0.000"}s duration
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-gray-500 text-sm">No trace data available.</div>
                  )}
                </motion.div>


              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const ConversationsHistory = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [selectedConversation, setSelectedConversation] = useState<any>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const {
    getConversationHistory,
    loading,
    error,
    data: apiData,
    clearError,
  } = useConversationHistory();

  const conversationData = apiData ? transformConversationData(apiData) : [];
  const totalItems = conversationData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = conversationData.slice(startIndex, endIndex);

  useEffect(() => {
    const loadData = async () => {
      try {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 7);

        await getConversationHistory({
          start: startDate.toISOString(),
          end: endDate.toISOString(),
        });
      } catch (err) {
        console.error('Error loading conversation history:', err);
      }
    };

    loadData();
  }, []);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleViewConversation = (conversation: any) => {
    setSelectedConversation(conversation);
    setIsPanelOpen(true);
  };

  const handleClosePanel = () => {
    setIsPanelOpen(false);
    setSelectedConversation(null);
  };

  const formatTokens = (tokens: number) => {
    return tokens.toLocaleString();
  };

  const formatDuration = (duration: number) => {
    return `${duration}s`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center mb-4">
        <MessagesSquare className="mr-3 text-primary" size={32} />
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Conversation History
          </h1>
          <p className="text-lg text-muted-foreground">
            View conversations, including messages, tokens, duration, and execution traces
          </p>
        </div>
      </div>

      {loading && (
        <div className="flex flex-col justify-center items-center py-12">
          <Loader2 className="w-10 h-10 text-primary animate-spin mb-4" />
          <span className="text-muted-foreground font-medium">Loading conversations...</span>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 rounded-md border border-red-200">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3 text-destructive">
              <h3 className="text-sm font-bold">Error loading conversations</h3>
              <div className="mt-2 text-sm">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={clearError}
                  className="px-3 py-2 text-sm font-medium text-destructive bg-destructive/10 rounded-md hover:bg-destructive/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-destructive"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && (
        <div className="overflow-hidden bg-background rounded-lg shadow border border-border">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-muted-foreground uppercase">
                    Thread ID
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-muted-foreground uppercase">
                    User
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-muted-foreground uppercase">
                    Preview
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-right text-muted-foreground uppercase">
                    Messages
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-right text-muted-foreground uppercase">
                    Tokens
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-right text-muted-foreground uppercase">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-center text-muted-foreground uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-muted-foreground uppercase">
                    Started
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-center text-muted-foreground uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-background divide-y divide-border">
                {paginatedData.length > 0 ? (
                  paginatedData.map((thread) => (
                    <tr key={thread.id} className="hover:bg-muted/50">
                      <td className="px-6 py-4 font-mono text-sm text-foreground whitespace-nowrap">
                        {thread.id}
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground whitespace-nowrap">
                        {thread.user}
                      </td>
                      <td className="px-6 py-4 max-w-xs text-sm text-foreground truncate">
                        {thread.preview}
                      </td>
                      <td className="px-6 py-4 text-sm text-right text-foreground whitespace-nowrap">
                        {thread.messages}
                      </td>
                      <td className="px-6 py-4 text-sm text-right text-foreground whitespace-nowrap">
                        {formatTokens(thread.tokens)}
                      </td>
                      <td className="px-6 py-4 text-sm text-right text-foreground whitespace-nowrap">
                        {formatDuration(thread.duration)}
                      </td>
                      <td className="px-6 py-4 text-center whitespace-nowrap">
                        <StatusBadge status={thread.status} />
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground whitespace-nowrap">
                        {thread.started}
                      </td>
                      <td className="px-6 py-4 text-center whitespace-nowrap">
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const originalConversation = apiData?.find(
                                (conv) =>
                                  conv.threadId?.substring(0, 12) +
                                  '...' +
                                  conv.threadId?.substring(conv.threadId.length - 4) ===
                                  thread.id,
                              );
                              if (originalConversation) {
                                handleViewConversation(originalConversation);
                              }
                            }}
                            className="flex gap-1 items-center"
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </Button>
                        </motion.div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="px-6 py-4 text-sm text-center text-muted-foreground">
                      No conversations found in the selected date range
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <Pagination
              // @ts-ignore
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              itemName="threads"
              showFirstLast={false}
              maxVisiblePages={5}
            />
          )}
        </div>
      )}

      {/* Conversation Panel */}
      <ConversationPanel
        conversation={selectedConversation}
        isOpen={isPanelOpen}
        onClose={handleClosePanel}
      />
    </div>
  );
};
